import React from "react"
import usePublicCounters from "./hooks/usePublicCounters.js"
import TickingNumber from "./components/TickingNumber.jsx"
import CountersStrip from "./components/CountersStrip.jsx"
import WhatItDoes from "./components/WhatItDoes.jsx"
import HowItWorks from "./components/HowItWorks.jsx"
import Pricing from "./components/Pricing.jsx"
import Faq from "./components/Faq.jsx"
import { formatterFor, rawValueFor } from "./lib/format.js"

function Header() {
  return (
    <header className="px-6 py-5 border-b border-[color:var(--color-rule)]">
      <div className="max-w-6xl mx-auto flex items-center justify-between">
        <a href="/" className="font-sans text-[15px] tracking-[-0.025em] font-medium">
          ShopShift<span className="text-[color:var(--color-ai)]">.</span>
        </a>
        <nav className="flex items-center gap-6 font-sans text-[13px] text-[color:var(--color-ink-secondary)]">
          <a href="#how-it-works" className="hidden sm:inline hover:text-[color:var(--color-ink-primary)]">
            How it works
          </a>
          <a href="#pricing" className="hidden sm:inline hover:text-[color:var(--color-ink-primary)]">
            Pricing
          </a>
          <a href="#faq" className="hidden sm:inline hover:text-[color:var(--color-ink-primary)]">
            FAQ
          </a>
          <a
            href="/users/sign_in"
            className="hover:text-[color:var(--color-ink-primary)]"
          >
            Sign in
          </a>
        </nav>
      </div>
    </header>
  )
}

function Hero() {
  const { byName } = usePublicCounters()
  const headline = byName.get("additional_conversions")
  const totalVisits = byName.get("total_visits")
  const aiCalls = byName.get("ai_calls_total")

  return (
    <section className="max-w-6xl mx-auto px-6 pt-24 pb-32">
      <p className="font-mono text-[10px] tracking-[0.15em] uppercase text-[color:var(--color-ai)] mb-8">
        § 01 · Real numbers from real shops
      </p>

      <h1 className="font-sans font-medium text-[color:var(--color-ink-primary)] text-[120px] tracking-[-0.05em] leading-[0.92]">
        <TickingNumber value={rawValueFor(headline)} format={formatterFor(headline?.unit)} />
      </h1>
      <p className="mt-4 font-sans text-[24px] tracking-[-0.02em] text-[color:var(--color-ink-secondary)] max-w-2xl leading-[1.3]">
        additional conversions caused by ShopShift's AI, across every webshop running it.
      </p>

      <div className="mt-12 flex flex-wrap items-baseline gap-x-10 gap-y-4 font-mono text-[12px] tracking-[0.04em] text-[color:var(--color-ink-tertiary)]">
        <span>
          <TickingNumber value={rawValueFor(totalVisits)} format={formatterFor(totalVisits?.unit)} /> visits tracked
        </span>
        <span className="text-[color:var(--color-ink-faint)]">·</span>
        <span>
          <TickingNumber value={rawValueFor(aiCalls)} format={formatterFor(aiCalls?.unit)} /> AI calls run
        </span>
        <span className="text-[color:var(--color-ink-faint)]">·</span>
        <span className="inline-flex items-center gap-1.5">
          <span
            className="inline-block w-1.5 h-1.5 rounded-full bg-[color:var(--color-winner-dot)]"
            style={{ animation: "pulse 2s ease-in-out infinite" }}
          />
          Streaming
        </span>
      </div>

      <div className="mt-16 flex items-center gap-4 flex-wrap">
        <a
          href="/users/sign_up"
          className="inline-flex items-center bg-[color:var(--color-brand)] text-white font-sans text-[14px] font-medium px-5 py-3 rounded-[var(--radius-button)] hover:opacity-90 transition-opacity"
        >
          Start your 30-day free trial
        </a>
        <a
          href="#how-it-works"
          className="font-sans text-[14px] font-medium text-[color:var(--color-ai)] hover:underline"
        >
          How it works →
        </a>
      </div>
      <p className="mt-4 font-mono text-[11px] tracking-[0.04em] text-[color:var(--color-ink-faint)]">
        Card required · no charge during trial · cancel any time
      </p>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>
    </section>
  )
}

function FooterColumn({ heading, children }) {
  return (
    <div className="flex flex-col gap-3">
      <p className="font-mono text-[10px] tracking-[0.15em] uppercase text-[color:var(--color-ink-tertiary)]">
        {heading}
      </p>
      {children}
    </div>
  )
}

function FooterLink({ href, children }) {
  return (
    <a
      href={href}
      className="font-sans text-[13px] text-[color:var(--color-ink-secondary)] hover:text-[color:var(--color-ink-primary)]"
    >
      {children}
    </a>
  )
}

function Footer() {
  return (
    <footer className="border-t border-[color:var(--color-rule)] bg-[color:var(--color-surface-card)] mt-auto">
      <div className="max-w-6xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-[1.5fr_1fr_1fr_1fr] gap-12">
          <div className="flex flex-col gap-4">
            <a href="/" className="font-sans text-[15px] tracking-[-0.025em] font-medium">
              ShopShift<span className="text-[color:var(--color-ai)]">.</span>
            </a>
            <p className="font-sans text-[13px] text-[color:var(--color-ink-secondary)] leading-[1.6] max-w-xs">
              AI-powered conversion optimization for webshops. 30 days free, then $99 a month. Cancel any time.
            </p>
            <p className="font-mono text-[11px] tracking-[0.04em] text-[color:var(--color-ink-faint)] mt-4">
              Operated by SVEA IT · CVR 36606061 · Denmark
            </p>
          </div>

          <FooterColumn heading="Product">
            <FooterLink href="#how-it-works">How it works</FooterLink>
            <FooterLink href="/pricing">Pricing</FooterLink>
            <FooterLink href="/learn">Learn</FooterLink>
            <FooterLink href="#faq">FAQ</FooterLink>
            <FooterLink href="/users/sign_up">Start free trial</FooterLink>
          </FooterColumn>

          <FooterColumn heading="Legal">
            <FooterLink href="/terms">Terms</FooterLink>
            <FooterLink href="/privacy">Privacy</FooterLink>
            <FooterLink href="/cancellation-and-refunds">Cancellation</FooterLink>
            <FooterLink href="/subprocessors">Subprocessors</FooterLink>
          </FooterColumn>

          <FooterColumn heading="Company">
            <FooterLink href="/contact">Contact</FooterLink>
            <FooterLink href="mailto:support@shopshift.io">support@shopshift.io</FooterLink>
            <FooterLink href="/users/sign_in">Sign in</FooterLink>
          </FooterColumn>
        </div>

        <div className="mt-16 pt-8 border-t border-[color:var(--color-rule)] flex items-center justify-between flex-wrap gap-4">
          <p className="font-mono text-[11px] text-[color:var(--color-ink-faint)]">
            © {new Date().getFullYear()} SVEA IT (CVR 36606061)
          </p>
          <p className="font-mono text-[11px] text-[color:var(--color-ink-faint)]">
            v0.1
          </p>
        </div>
      </div>
    </footer>
  )
}

export default function App({ priceCents = 9900 }) {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1">
        <Hero />
        <CountersStrip />
        <WhatItDoes />
        <HowItWorks />
        <Pricing priceCents={priceCents} />
        <Faq />
      </main>
      <Footer />
    </div>
  )
}
